<template>
  <div class="c-assigned-canvas-management w-full h-full">
    <CAssignedCanvasesManagementToolbar
      ref="toolbarWrapper"
      :custom-filter-components="dataFilterComponents"
      :hide-view="!launchDarkly.allow_canvases_tables_columns_settings"
      :is-loading-items="isLoading"
      :metadata-filter-values="filters"
      :search="search"
      :tableColumnsSettings="tableColumnsSettings"
      @move="onColumnsMove"
      @restoreColumns="restoreColumns"
      @search="onTableChange('searchChanged', $event)"
      @select="onColumnsSelect"
      @update:filter-values="onTableChange('filterValuesChanged', $event)"
    />

    <CTable
      ref="tableRef"
      class="mt-3"
      :columns="launchDarkly.allow_canvases_tables_columns_settings ? columnsFromSettings : columns"
      :data="filteredData"
      :initialSorting="[{ id: 'modified_at', desc: true }]"
      :loading="isLoadingTable"
      :page-index="fetchingParams.page - 1"
      :page-size="fetchingParams.page_size"
      :page-sizes="DEFAULT_PAGE_SIZE_OPTIONS"
      :scroller-class="noTabs ? 'w-full overflow-auto max-h-[calc(100vh-314px)]' : undefined"
      :scroller-style="{
        height: `calc(100vh - 320px - ${canvasManagementToolbarHeight || 0}px + ${noTabs ? 36 : 0}px)`,
      }"
      :selectable="false"
      server-side
      :total-count="totalCount"
      @page-changed="onTableChange('pageChanged', $event)"
      @page-size-changed="onTableChange('pageSizeChanged', $event)"
      @sort-changed="onTableChange('sortChanged', $event)"
    />
    <slot />
  </div>
</template>

<script setup lang="tsx">
import { computed, onMounted, ref, inject, watch, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { type ColumnDef, createColumnHelper, type PaginationState, type SortingState } from '@tanstack/table-core'
import { isEqual, omit } from 'lodash-es'
import { useElementSize } from '@vueuse/core'

import { DEFAULT_PAGE_SIZE_OPTIONS } from '@lib/constants/tables.const'
import CTable from '@lib/components/CTable/CTable.vue'
import CTableInput from '@lib/components/CTableInput/CTableInput.vue'
import CTableTag from '@lib/components/CTableTag/CTableTag.vue'
import CTableMore from '@lib/components/CTableMore/CTableMore.vue'
import CAssignedCanvasesManagementToolbar from '@lib/components/assignedCanvases/CAssignedCanvasesManagementToolbar.vue'
import CSavedCanvasesManagementActionsCell from '@lib/components/savedCanvases/CSavedCanvasesManagementActionsCell.vue'
import CSavedCanvasesManagementUsedTemplateCell from '@lib/components/savedCanvases/CSavedCanvasesManagementUsedTemplateCell.vue'
import CSavedCanvasesManagementOwnerCell from '@lib/components/savedCanvases/CSavedCanvasesManagementOwnerCell.vue'
import CSwitch from '@lib/components/CSwitch/CSwitch.vue'
import CThumbnail from '@lib/components/CThumbnail/CThumbnail.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import type { PaginatedData } from '@lib/types/paginatedData'
import type { GetCanvasesParams } from '@lib/types/canvases'
import { useCanvasStandardFilters } from '@lib/composables/useCanvasStandardFilters'
import { getVisibleColumns } from '@lib/util/canvas-manager.util'
import { formatDateTime } from '@lib/util/date'
import { normalizeFilterParams } from '@lib/components/filters/filter.util'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { useTableColumnsSettings } from '@lib/composables/tableColumnsSettings.use'
import useCanvasTablesPagination from '@lib/composables/useCanvasTablesPagination'
import type { CanvasRetrieve, User } from '@openapi'

const DEFAULT_ORDERING = '-modified_at'

interface Props {
  fetcher: (
    params: GetCanvasesParams & { filters?: Record<string, any>; fields?: string },
  ) => Promise<PaginatedData<CanvasRetrieve>>
  templatesFetcher?: (
    params: GetCanvasesParams & { filters?: Record<string, any>; fields?: string },
  ) => Promise<PaginatedData<CanvasRetrieve>>
  byIdFetcher: (params: { id: CanvasRetrieve['id']; fields?: string }) => Promise<CanvasRetrieve>
  deleter: (id: CanvasRetrieve['id']) => Promise<CanvasRetrieve>
  downloadCanvas: (canvas: CanvasRetrieve, fileFormat?: 'pdf' | 'pptx') => Promise<void>
  myUser?: User | null
  noTabs?: boolean
  storageKey?: string
  accounts: { label: string; value: string }[]
  accountsLoading: boolean
  track?: (eventName: string, payload: any) => void
  isCRM: boolean
  hideEdit?: boolean
  isAnyTypeOfAdmin?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  open: [canvas: CanvasRetrieve, newTab?: boolean]
  openTemplate: [canvasId: CanvasRetrieve['id'], templateId: CanvasRetrieve['id']]
  edit: [canvas: CanvasRetrieve]
  'account-search': [value: string]
}>()

const { t } = useI18n()
const themeVars = useThemeVars()

const tableRef = ref<InstanceType<typeof CTable> | null>(null)
const toolbarWrapper = ref<HTMLElement | null>(null)
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const isLoading = ref(false)
const fetchedData = ref<CanvasRetrieve[]>([])
const totalCount = ref(0)
const availableTemplates = ref<CanvasRetrieve[]>([])

const templateOptions = computed(() =>
  availableTemplates.value.map((t) => ({ label: t.name, value: t.id })).sort((a, b) => a.label.localeCompare(b.label)),
)

async function fetchTemplates() {
  if (!props.templatesFetcher) return
  try {
    const { results } = await props.templatesFetcher({
      is_template: true,
      is_section: false,
      page: 1,
      page_size: 1000,
      fields: 'id,name',
    })
    availableTemplates.value = results
  } catch (e) {
    console.error('Failed to fetch templates for filter:', e)
  }
}

const { filterComponents: dataFilterComponents } = useCanvasStandardFilters({
  includeTemplateFilter: true,
  includeOwnerFilter: true,
  includeTagsFilter: true,
  includeDraftFilter: true,
  templateOptions,
})

const { height: canvasManagementToolbarHeight } = useElementSize(toolbarWrapper)

const {
  search,
  pageSize,
  selectedFilters: filters,
  page,
  ordering,
} = useCanvasTablesPagination({
  storageKey: props.storageKey,
  initialFilters: {},
  initialOrdering: DEFAULT_ORDERING,
})

const fetchingParams = computed(() => ({
  page: page.value,
  page_size: pageSize.value,
  ordering: ordering.value,
  search: search.value,
  filters: filters.value,
}))

const isLoadingTable = computed(() => isLoading.value)

const fetchFields = [
  'id',
  'name',
  'tags',
  'is_draft',
  'modified_at',
  'updated_at',
  'template',
  'is_template',
  'is_section',
  'is_block',
  'thumbnail_url',
  'metadata',
  'owned_by',
  ...(props.isCRM ? ['account'] : []),
]

const filteredData = computed(() => {
  if (!search.value) return fetchedData.value

  const currSearch = (search.value ?? '').toLocaleLowerCase()

  return fetchedData.value.filter(
    (c) =>
      (c.name.toLocaleLowerCase().includes(currSearch) ||
        c?.tags?.some((tag) => tag.toLocaleLowerCase().includes(currSearch))) ??
      false,
  )
})

// Column definitions
const columnHelper = createColumnHelper<CanvasRetrieve>()

const defaultColumns = [
  columnHelper.accessor((row) => row.name, {
    id: 'name',
    cell: (cellCtx) => (
      <CTableInput
        modelValue={cellCtx.getValue()}
        readonly
        state="view"
        valueClass="font-700 underline unerline-solid"
        class="cursor-pointer"
        onClick={(e: MouseEvent) => {
          emit('open', cellCtx.row.original, e.metaKey || e.ctrlKey)
        }}
      >
        {{
          prefix: () => {
            const thumbnailUrl = cellCtx.row.original.thumbnail_url ?? cellCtx.row.original.metadata?.thumbnail

            return (
              <div class="flex items-center">
                <CThumbnail
                  class="mr-2"
                  thumbnailUrl={thumbnailUrl}
                  item={cellCtx.row.original}
                  themeVars={themeVars.value}
                  enableThumbnails={false}
                />
              </div>
            )
          },
        }}
      </CTableInput>
    ),
    header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.nameTh'),
  }),
  ...(props.isCRM
    ? [
        columnHelper.accessor((row) => row.account, {
          id: 'account',
          cell: (cellCtx) => (
            <CTableMore
              modelValue={[{ label: cellCtx.row.original.account?.name ?? '-', key: cellCtx.row.original.account?.id }]}
              rowIndex={cellCtx.row.index}
              isClickable={false}
            />
          ),
          header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.accountTh'),
          enableSorting: false,
        }),
      ]
    : []),
  columnHelper.accessor((row) => row.tags, {
    id: 'tags',
    cell: (cellCtx) => (
      <CTableTag readonly modelValue={cellCtx.getValue() ?? []} state="view" rowIndex={cellCtx.row.index} />
    ),
    header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.tagsTh'),
    enableSorting: false,
    meta: {
      classes: launchDarkly.value.enable_short_tag_column_in_tables ? '!max-w-[200px]' : undefined,
    },
  }),
  columnHelper.accessor((row) => row.is_draft, {
    id: 'is_draft',
    cell: (cellCtx) => (
      <div class="flex items-center">
        <CSwitch
          disabled
          checked={!cellCtx.getValue()}
          themeOverrides={{
            railColorActive: themeVars.value.success,
            railColor: themeVars.value.primary6,
          }}
        />
      </div>
    ),
    header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagementStatusCell.status.ready'),
    enableSorting: false,
  }),
  columnHelper.accessor((row) => row.owned_by, {
    id: 'owned_by__name',
    cell: (cellCtx) => {
      const email = cellCtx.row.original.owned_by?.email ?? ''
      const ownedByText = cellCtx.row.original.owned_by?.name || email || ''
      return (
        <CSavedCanvasesManagementOwnerCell
          class="min-w-[280px]"
          editable={false}
          myUser={props.myUser}
          owner={{ name: ownedByText, email, value: cellCtx.row.original.owned_by?.id }}
          isEditing={false}
          isUpdating={false}
        />
      )
    },
    header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.ownedByTh'),
  }),
  columnHelper.accessor((row) => row.modified_at, {
    id: 'modified_at',
    cell: (cellCtx) => <div class="min-w-[150px]">{formatDateTime(cellCtx.getValue()) as string}</div>,
    header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.modifiedAtTh'),
  }),
  columnHelper.accessor((row) => row.template, {
    id: 'template',
    cell: (cellCtx) => {
      return (
        <CSavedCanvasesManagementUsedTemplateCell
          name={cellCtx.row.original?.template?.name}
          hideCreate
          disableOpen={props.noTabs}
          onOpen={() => emit('openTemplate', cellCtx.row.original.id, cellCtx.row.original.template.id)}
        />
      )
    },
    header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.templateUsedTh'),
    enableSorting: false,
  }),
  columnHelper.accessor((row) => row.name, {
    id: 'actions',
    cell: (cellCtx) => {
      return (
        <CSavedCanvasesManagementActionsCell
          canvasId={cellCtx.cell.row.original.id}
          track={props.track}
          hideLock
          hideCopy
          hideShare
          hideEdit={props.hideEdit}
          hideDelete
          hideViewNewTab={false}
          isCopying={false}
          isDeleting={false}
          isSharing={false}
          isDownloading={false}
          hideOwnerReassign
          onEdit={() => emit('edit', cellCtx.cell.row.original)}
          onView={() => emit('open', cellCtx.cell.row.original, false)}
          onViewNewTab={() => emit('open', cellCtx.cell.row.original, true)}
          onDownloadCanvas={(fileFormat) => props.downloadCanvas(cellCtx.cell.row.original, fileFormat)}
        />
      )
    },
    header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.actionsTh'),
    enableSorting: false,
  }),
] as ColumnDef<Record<string, any>, any>[]

const columns = computed(() =>
  getVisibleColumns(launchDarkly.value.canvas_manager_table_columns, defaultColumns, 'canvases'),
)

// Table column settings
const TABLE_COLUMNS_SETTINGS_KEY = `${props.storageKey}-table-columns`

const { tableColumnsSettings, columnsFromSettings, onColumnsMove, onColumnsSelect, restoreColumns } =
  useTableColumnsSettings(TABLE_COLUMNS_SETTINGS_KEY, columns.value)

async function fetch(params: {
  page: number
  page_size: number
  ordering: string
  search: string
  filters: Record<string, any>
  account__id?: string
}) {
  try {
    isLoading.value = true
    const requestParams = params.search ? params : omit(params, 'search')

    const dataFilters: Record<string, any> = {}
    const metaDataFilters: Record<string, any> = {}

    Object.entries(requestParams.filters).forEach(([key, value]) => {
      if (dataFilterComponents.value.some((component) => component.name === key)) {
        dataFilters[key] = value
      } else {
        metaDataFilters[key] = value
      }
    })

    const normalizedMetaDataFilters = normalizeFilterParams(metaDataFilters)

    const { results, count } = await props.fetcher({
      ...requestParams,
      filters: normalizedMetaDataFilters,
      ...dataFilters,
      is_section: false,
      is_template: false,
      fields: fetchFields.join(','),
    })

    totalCount.value = count
    fetchedData.value = results
  } catch (e) {
    console.error(e)
  } finally {
    isLoading.value = false
  }
}

function onTableChange(
  event: 'pageChanged' | 'pageSizeChanged' | 'sortChanged' | 'searchChanged' | 'itemsChanged' | 'filterValuesChanged',
  state?: number | SortingState | PaginationState | string | Record<string, any>,
) {
  const initialParams = { ...fetchingParams.value }
  let change
  switch (event) {
    case 'pageChanged':
      change = state as PaginationState
      page.value = change.pageIndex + 1
      break
    case 'pageSizeChanged':
      change = state as number
      pageSize.value = change ?? fetchingParams.value.page_size
      page.value = 1
      break
    case 'sortChanged':
      ;[change] = state as SortingState
      ordering.value = change?.desc ? '-' + change.id : (change?.id ?? DEFAULT_ORDERING)
      page.value = 1
      break
    case 'searchChanged':
      change = state as string
      search.value = change
      page.value = 1
      break
    case 'itemsChanged':
      page.value = 1
      break
    case 'filterValuesChanged':
      change = state as Record<string, any>
      filters.value = change
      page.value = 1
      break
  }

  if (event === 'itemsChanged' || !isEqual(initialParams, fetchingParams.value)) {
    fetch(fetchingParams.value)
  }
}

watch(
  fetchedData,
  () => {
    // Hook for future data processing if needed
  },
  { immediate: true },
)

onMounted(async () => {
  try {
    isLoading.value = true
    fetchTemplates()
    await fetch(fetchingParams.value)
  } catch (e) {
    console.error(e)
    isLoading.value = false
  }
})

defineExpose({
  refetch: () => fetch(fetchingParams.value),
})
</script>

<style scoped>
.c-assigned-canvas-management {
  display: flex;
  flex-direction: column;
}
</style>
